import { X, Pin, Settings } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { TabBarProps } from '../types';
import styles from './TabBar.module.css';

/**
 * TabBar - Componente de presentación PURO
 * 
 * RESPONS ABILIDAD:
 * - Renderizar tabs visualmente
 * - Emitir eventos via callbacks
 * - Manejar estado local de UI (dropdown abierto/cerrado)
 * 
 * NO debe:
 * - Usar useNavigate()
 * - Llamar directamente a store actions
 * - Tener lógica de negocio
 */
export const TabBar = ({
    tabs,
    activeTabId,
    isSidebarCollapsed = false,
    onTabClick,
    onTabClose,
    onMenuAction,
}: TabBarProps) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    /**
     * Cerrar menú al hacer click fuera
     * IMPORTANTE: useEffect DEBE estar ANTES de cualquier early return
     */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        };

        if (menuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [menuOpen]);

    // Early return DESPUÉS de todos los hooks (Rules of Hooks)
    if (tabs.length === 0) return null;

    /**
     * Renderiza un ícono de Lucide React dinámicamente
     */
    const renderIcon = (iconName?: string) => {
        if (!iconName) return null;
        const IconComponent = (LucideIcons as any)[iconName];
        if (!IconComponent) return null;
        return <IconComponent size={20} strokeWidth={2} />;
    };

    /**
     * Handler para cerrar tab - delega al callback padre
     */
    const handleCloseTab = (tabId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onTabClose(tabId);
    };

    /**
     * Handlers del menú - emiten eventos hacia arriba
     */
    const handleCloseAll = () => {
        onMenuAction({ type: 'CLOSE_ALL' });
        setMenuOpen(false);
    };

    const handleCloseOthers = () => {
        if (activeTabId) {
            onMenuAction({ type: 'CLOSE_OTHERS', tabId: activeTabId });
            setMenuOpen(false);
        }
    };

    const handleCloseRight = () => {
        if (activeTabId) {
            onMenuAction({ type: 'CLOSE_RIGHT', tabId: activeTabId });
            setMenuOpen(false);
        }
    };

    const handleReloadActive = () => {
        if (activeTabId) {
            onMenuAction({ type: 'RELOAD_ACTIVE', tabId: activeTabId });
            setMenuOpen(false);
        }
    };

    return (
        <div className={`${styles.tabBar} ${isSidebarCollapsed ? styles.sidebarCollapsed : ''}`}>
            <div className={styles.tabList}>
                {tabs.map((tab) => {
                    const isActive = tab.id === activeTabId;

                    return (
                        <div
                            key={tab.id}
                            className={`${styles.tab} ${isActive ? styles.active : ''} ${tab.isPinned ? styles.pinned : ''
                                }`}
                            onClick={() => onTabClick(tab.id)}
                            role="tab"
                            aria-selected={isActive}
                            aria-label={`Tab ${tab.title}`}
                        >
                            {tab.icon && (
                                <span className={styles.icon}>{renderIcon(tab.icon)}</span>
                            )}

                            <span className={styles.title} title={tab.title}>
                                {tab.title}
                            </span>

                            {tab.isPinned && (
                                <Pin size={12} className={styles.pinIcon} />
                            )}

                            {tab.closeable && (
                                <button
                                    className={styles.closeBtn}
                                    onClick={(e) => handleCloseTab(tab.id, e)}
                                    aria-label={`Cerrar ${tab.title}`}
                                    type="button"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Menú de acciones */}
            <div className={styles.tabActions} ref={menuRef}>
                <button
                    className={styles.menuBtn}
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label="Opciones de tabs"
                    type="button"
                >
                    <Settings size={18} />
                </button>

                {/* Dropdown menu */}
                {menuOpen && (
                    <div className={styles.dropdownMenu}>
                        <button
                            className={styles.menuItem}
                            onClick={handleCloseOthers}
                            disabled={tabs.filter((t) => t.closeable).length <= 1}
                        >
                            Cerrar otros tabs
                        </button>
                        <button
                            className={styles.menuItem}
                            onClick={handleCloseRight}
                            disabled={
                                tabs.findIndex((t) => t.id === activeTabId) >= tabs.length - 1
                            }
                        >
                            Cerrar tabs a la derecha
                        </button>
                        <div className={styles.menuDivider} />
                        <button
                            className={styles.menuItem}
                            onClick={handleCloseAll}
                            disabled={tabs.filter((t) => t.closeable).length === 0}
                        >
                            Cerrar todos los tabs
                        </button>
                        <div className={styles.menuDivider} />
                        <button
                            className={styles.menuItem}
                            onClick={handleReloadActive}
                            disabled={!activeTabId}
                        >
                            Recargar tab activo
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
